import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Users, 
  Loader2,
  Mail,
  Shield,
  ToggleLeft,
  ToggleRight,
  Clock,
  Plus,
  Edit,
  Key,
  IndianRupee,
  Eye,
  EyeOff,
  Search,
  Phone,
  Briefcase,
  MapPin,
  BadgeCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const DESIGNATIONS = ['SR. SE/Executive/TSI', 'TSM', 'ASM,SR ASM', 'ZSM/RSM'];

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  role: 'field_user',
  phone: '',
  designation: '',
  headquarter: '',
  employee_code: '',
  reporting_manager: '',
  mobile_expense: false
};

const UsersPage = () => {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [passwordForm, setPasswordForm] = useState({
    userId: '',
    userName: '',
    newPassword: ''
  });

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await api.put(`/users/${userId}/toggle-status`);
      toast.success('User status updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData(EMPTY_FORM);
    setDialogOpen(true);
  };

  // Saving a designation re-prices that user's open TADA on its own. This is for
  // the ones changed before that existed, and for re-running it after a rate
  // card edit - the claims are already raised and nothing else would revisit them.
  const [repricing, setRepricing] = useState(null);

  const handleRepriceTada = async (user) => {
    if (!window.confirm(
      `Re-price ${user.name}'s TADA at their current designation (${user.designation || 'not set'})?\n\n`
      + 'Only Headquarter and Ex-Station claims in months that are still open change. '
      + 'Outstation claims and anything inside the TADA lock are left alone.'
    )) return;
    setRepricing(user.id);
    try {
      const { data } = await api.post(`/users/${user.id}/reprice-tada`);
      const t = data || {};
      if (!t.repriced) {
        toast.success(`Nothing to change — ${t.claims_seen ?? 0} claim(s) already correct`);
      } else {
        const sign = t.net_change > 0 ? '+' : '';
        toast.success(`${t.repriced} claim(s) re-priced, net ${sign}₹${t.net_change}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not re-price');
    } finally {
      setRepricing(null);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'field_user',
      phone: user.phone || '',
      designation: user.designation || '',
      headquarter: user.headquarter || '',
      employee_code: user.employee_code || '',
      reporting_manager: user.reporting_manager || '',
      mobile_expense: !!user.mobile_expense
    });
    setDialogOpen(true);
  };

  const handleChangePassword = (user) => {
    setPasswordForm({
      userId: user.id,
      userName: user.name,
      newPassword: ''
    });
    setPasswordDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { password, ...profile } = formData;
      if (editingUser) {
        // Update user
        await api.put(`/users/${editingUser.id}`, profile);
        toast.success('User updated successfully');
      } else {
        // Create new user
        if (!formData.password) {
          toast.error('Password is required for new user');
          return;
        }
        await api.post('/users', formData);
        toast.success('User created successfully');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwordForm.newPassword) {
      toast.error('New password is required');
      return;
    }
    try {
      await api.put(`/users/${passwordForm.userId}/password`, {
        new_password: passwordForm.newPassword
      });
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const term = search.trim().toLowerCase();
  const filteredUsers = !term
    ? users
    : users.filter((u) =>
        [u.name, u.email, u.employee_code, u.designation, u.headquarter, u.reporting_manager]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(term))
      );

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 fade-in" data-testid="users-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
            <p className="text-sm text-slate-500">
              {users.length} user{users.length === 1 ? '' : 's'}
              {term && ` · ${filteredUsers.length} matching`}
            </p>
          </div>
          <Button
            onClick={handleAddUser}
            data-testid="add-user-btn"
            className="h-10 px-4 rounded-xl bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, employee code, designation or HQ"
            className="h-11 pl-9 rounded-xl bg-white"
            data-testid="user-search-input"
          />
        </div>

        {/* Users List */}
        <div className="space-y-3">
          {filteredUsers.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No users found</p>
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div
                key={user.id}
                className="bg-white rounded-2xl border border-slate-100 p-4 hover:shadow-md transition-all"
                data-testid={`user-card-${user.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-12 h-12 rounded-xl flex items-center justify-center text-white font-semibold",
                      user.role === 'admin' ? "bg-violet-600" : "bg-sky-600"
                    )}>
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-slate-900">{user.name}</h3>
                        {user.employee_code && (
                          <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                            {user.employee_code}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                        <span className="text-sm text-slate-500 break-all">{user.email}</span>
                      </div>
                      {user.phone && (
                        <div className="flex items-center gap-2 mt-1">
                          <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                          <span className="text-sm text-slate-500">{user.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1",
                          user.is_superadmin
                            ? "bg-amber-100 text-amber-700"
                            : user.role === 'admin' ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"
                        )}>
                          <Shield className="w-3 h-3" />
                          {user.is_superadmin ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'Field User'}
                        </span>
                        {user.designation && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Briefcase className="w-3 h-3 text-slate-400" />
                            {user.designation}
                          </span>
                        )}
                        {user.headquarter && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            {user.headquarter}
                          </span>
                        )}
                        {user.reporting_manager && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <BadgeCheck className="w-3 h-3 text-slate-400" />
                            {user.reporting_manager}
                          </span>
                        )}
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(user.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "px-2 py-1 rounded-full text-xs font-medium",
                      user.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                    )}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditUser(user)}
                      className="h-8 w-8"
                      data-testid={`edit-user-${user.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleChangePassword(user)}
                      className="h-8 w-8 text-amber-600"
                      data-testid={`change-password-${user.id}`}
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRepriceTada(user)}
                      disabled={repricing === user.id}
                      title="Re-price this user's open TADA at their current designation"
                      className="h-8 rounded-lg text-sky-700 border-sky-200"
                      data-testid={`reprice-tada-${user.id}`}
                    >
                      <IndianRupee className="w-3.5 h-3.5 mr-1" />
                      {repricing === user.id ? 'Re-pricing…' : 'Re-price TADA'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleStatus(user.id)}
                      className="rounded-lg"
                      data-testid={`toggle-user-${user.id}`}
                    >
                      {user.is_active ? (
                        <ToggleRight className="w-6 h-6 text-emerald-600" />
                      ) : (
                        <ToggleLeft className="w-6 h-6 text-slate-400" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit User Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                  className="h-12 rounded-xl"
                  data-testid="user-name-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email"
                  className="h-12 rounded-xl"
                  data-testid="user-email-input"
                  required
                />
              </div>
              {!editingUser && (
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter password"
                      className="h-12 pr-10 rounded-xl"
                      data-testid="user-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="field_user">Field User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Employee Code</Label>
                  <Input
                    value={formData.employee_code}
                    onChange={(e) => setFormData({ ...formData, employee_code: e.target.value })}
                    placeholder="e.g. 000768"
                    className="h-12 rounded-xl"
                    data-testid="user-empcode-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="10-digit mobile"
                    className="h-12 rounded-xl"
                    data-testid="user-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Designation</Label>
                <Select
                  value={formData.designation}
                  onValueChange={(value) => setFormData({ ...formData, designation: value })}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="user-designation-select">
                    <SelectValue placeholder="Select designation" />
                  </SelectTrigger>
                  <SelectContent>
                    {DESIGNATIONS.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Saving a changed designation re-prices on its own. This is for
                    a designation changed before that existed, or after the rate
                    card itself was edited - claims already raised that nothing
                    else would revisit. */}
                {editingUser && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
                    <p className="text-xs text-slate-600">
                      Saving a new designation re-prices this user's open TADA automatically.
                      Use this if the designation was changed earlier, or the rate card has moved.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleRepriceTada(editingUser)}
                      disabled={repricing === editingUser.id}
                      className="h-10 rounded-xl w-full"
                      data-testid="reprice-tada-dialog"
                    >
                      <IndianRupee className="w-4 h-4 mr-2" />
                      {repricing === editingUser.id ? 'Re-pricing…' : 'Re-price TADA now'}
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Headquarter</Label>
                <Input
                  value={formData.headquarter}
                  onChange={(e) => setFormData({ ...formData, headquarter: e.target.value })}
                  placeholder="e.g. KOLKATA"
                  className="h-12 rounded-xl"
                  data-testid="user-hq-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Reporting Manager (RSM / Sr. ASM)</Label>
                <Input
                  value={formData.reporting_manager}
                  onChange={(e) => setFormData({ ...formData, reporting_manager: e.target.value })}
                  placeholder="Manager name"
                  className="h-12 rounded-xl"
                  data-testid="user-manager-input"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={formData.mobile_expense}
                  onChange={(e) => setFormData({ ...formData, mobile_expense: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300"
                  data-testid="user-mobile-expense-checkbox"
                />
                Mobile expense applicable
              </label>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 h-11 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 rounded-xl bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="save-user-btn"
                >
                  {editingUser ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Change Password for {passwordForm.userName}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>New Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    placeholder="Enter new password"
                    className="h-12 pr-10 rounded-xl"
                    data-testid="new-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPasswordDialogOpen(false)}
                  className="flex-1 h-11 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="change-password-btn"
                >
                  Change Password
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UsersPage;
